<script lang="ts">
    import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
    import ModulesStatusesProvider from "@modules/base_module/ModulesStatusesProvider/ModulesStatusesProvider";
    import { map } from "rxjs";
    import { onMount } from "svelte";
    import { fly } from "svelte/transition";

    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import { LoggerInterface } from "@interfaces/LoggerInterface";

    import NudgesTheme from "../components/NudgesTheme.svelte";
    import { CTAW_TYPE } from "../types";
    import ApplyDiscountWidgetView from "./views/CTAW/ApplyDiscountWidgetView.svelte";
    import ButtonLinkWidgetView from "./views/CTAW/ButtonLinkWidgetView.svelte";
    import ButtonRedeemWidgetView from "./views/CTAW/ButtonRedeemWidgetView.svelte";
    import LoginOrSignupWidgetView from "./views/CTAW/LoginOrSignupWidgetView.svelte";
    import { nudgesCustomerRewardsState } from "./views/state/nudgesCustomerRewardsState";

    export let rewardsPopUpWidgetLauncherManager: RewardsPopUpWidgetLauncherManager;
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let modulesStatusesProvider: ModulesStatusesProvider;
    export let logger: LoggerInterface;

    const {
        nudges$,
        nudgesService,
        NudgesPopUpWidgetVisibilityManager,
        locale,
    } = nudgesCustomerRewardsState();

    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();
    const isNudgesPopUpWidgetOpen$ =
        NudgesPopUpWidgetVisibilityManager.isNudgesPopUpWidgetOpen$;

    const ctawType$ = nudges$.pipe(map((nudge) => nudge?.template.ctaw.type));

    onMount(() => {
        const nudgesSubscription = nudgesService
            .getLastNudgeWithoutView({ locale })
            .subscribe();

        const subscription = nudges$.subscribe((nudge) => {
            nudge && NudgesPopUpWidgetVisibilityManager.nudgesPopUpWidgetShow();
        });

        return () => {
            subscription.unsubscribe();
            nudgesSubscription.unsubscribe();
        };
    });

    const handleCloseModal = () => {
        $nudges$ &&
            $nudges$.nudge.id &&
            nudgesService.addViewed({
                nudgeId: $nudges$.nudge.id,
            });
        NudgesPopUpWidgetVisibilityManager.nudgesPopUpWidgetHide();
    };
</script>

{#if $rewardsBranding$}
    <NudgesTheme rewardsBranding={$rewardsBranding$}>
        {#if $nudges$ && $isNudgesPopUpWidgetOpen$}
            {@const nudges = $nudges$}
            <div
                transition:fly={{
                    x:
                        $rewardsBranding$.popUpWidget.launcher.position
                            .placement === "BR"
                            ? 200
                            : -200,
                    duration: 1000,
                }}
                class="gw-nudges-popup-widget-container gw-nudges-popup-widget-container--{$rewardsBranding$
                    .popUpWidget.launcher.position.placement}"
            >
                <Image
                    imgClassName="gw-nudges-popup-widget__image"
                    wrapperClassName="gw-nudges-popup-widget__image-wrapper"
                    alt={nudges.template.title}
                    src={nudges.template.icon}
                    objectFit="cover"
                />
                <div class="gw-nudges-popup-widget__content">
                    <h4 class="gw-nudges-popup-widget__title">
                        {nudges.template.title}
                    </h4>
                    <p class="gw-nudges-popup-widget__description">
                        {nudges.template.description}
                    </p>
                    {#if $ctawType$ === CTAW_TYPE.BUTTON_LINK}
                        <ButtonLinkWidgetView {nudges} />
                    {:else if $ctawType$ === CTAW_TYPE.LOGIN_OR_SIGN_UP}
                        <LoginOrSignupWidgetView {nudges} />
                    {:else if $ctawType$ === CTAW_TYPE.BUTTON_REDEEM_REWARD}
                        <ButtonRedeemWidgetView
                            {nudges}
                            {rewardsPopUpWidgetLauncherManager}
                        />
                    {:else if $ctawType$ === CTAW_TYPE.BUTTON_APPLY_DISCOUNT}
                        <ApplyDiscountWidgetView
                            {nudges}
                            {modulesStatusesProvider}
                            {logger}
                        />
                    {/if}
                </div>
                <ButtonWithIcon
                    on:click={handleCloseModal}
                    class="gw-nudges-popup-widget__close_btn"
                    ariaLabel="Close modal"
                >
                    <Icon height="22" width="22">
                        <CloseIcon />
                    </Icon>
                </ButtonWithIcon>
            </div>
        {/if}
    </NudgesTheme>
{/if}

<style lang="postcss" global>
    @import "../../../themes/globals.css";
    @import "../../../themes/default/default.css";

    .gw-nudges-popup-widget-container--BR {
        right: var(--gw-ng-space-5);
        flex-direction: row-reverse;

        .gw-select .gw-select-list {
            right: 0;
            left: auto;
        }
    }

    .gw-nudges-popup-widget-container--BL {
        left: var(--gw-ng-space-5);

        .gw-select .gw-select-list {
            right: auto;
            left: 0;
        }
    }
    .gw-nudges-popup-widget-container {
        display: flex;
        justify-content: space-between;
        background: #fff;
        box-shadow: 0 0 2px 2px var(--gw-ng-main-shadow);
        border-radius: var(--gw-ng-border-radius-base);
        padding: var(--gw-ng-space-5);
        z-index: var(--gw-z-index-drawer);
        position: fixed;
        bottom: 110px;
        width: 360px;

        @media all and (max-width: 550px) {
            width: auto;
        }

        .gw-nudges-popup-widget__content {
            flex: 1 1 auto;
            margin: 0 var(--gw-ng-space-5);
        }

        .gw-nudges-popup-widget__image-wrapper {
            max-width: 50px;
            max-height: 50px;
            width: 100%;
            --gw-image-backdrop-color: transparent;
        }

        .gw-nudges-popup-widget__title {
            margin: 0;
            color: var(--gw-ng-primary-color);
            font-size: var(--gw-font-size-sm);
            line-height: 1.2;
        }

        .gw-nudges-popup-widget__description {
            margin: 0;
            color: var(--gw-ng-primary-color);
            font-size: var(--gw-font-size-sm);
            line-height: 1.2;
            margin-top: var(--gw-ng-space-5);
        }

        .gw-nudges-popup-widget__close_btn {
            box-sizing: content-box;
            z-index: 2;
            padding: var(--gw-ng-space-5);
            color: var(--gw-ng-primary-color);
        }

        .gw-btn.gw-nudges-modal__active-button {
            background-color: var(--gw-nudges-buttons__theme-color);
            color: var(--gw-nudges-buttons__font-color);
            font-size: var(--gw-font-size-xs);
            margin-top: var(--gw-ng-space-5);
            border-radius: var(--gw-ng-border-radius-base);
            padding: var(--gw-ng-space-5);

            &:disabled {
                opacity: 0.3;
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .gw-btn.gw-nudges-modal__reward-redeem-active-button {
            background-color: var(--gw-nudges-buttons__theme-color);
            color: var(--gw-nudges-buttons__font-color);
            font-size: var(--gw-font-size-xs);
            margin-left: var(--gw-ng-space-5);
            border-radius: var(--gw-ng-border-radius-base);
            padding: var(--gw-ng-space-5);

            &:disabled {
                opacity: 0.3;
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .gw-nudges-modal__base-button {
            color: var(--gw-ng-primary-color);
            margin-left: var(--gw-ng-space-5);

            &:hover {
                text-decoration: underline;
            }
        }

        @media all and (max-width: 500px) {
            .gw-btn.gw-nudges-modal__reward-redeem-active-button {
                margin: var(--gw-ng-space-5) 0 0 0;
            }
        }

        @media all and (min-width: 590px) {
            .gw-select .gw-select-list {
                width: auto;
                min-width: 100%;
                max-width: 320px;
            }
        }

        .gw-select .gw-select-list {
            max-height: 100px;
            overflow-y: auto;
        }
    }
</style>
